import React, { Suspense, useState } from 'react';
import Button from 'components/Button';
import DropzoneWidget, { useFiles } from 'components/DropzoneWidget';
import FormContainer from 'components/LoanForm/FormContainer';
import ReactLoading from 'react-loading';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { useSelector } from 'react-redux';
import { getApplicationData } from 'selectors/getApplicationData';
import { updateApplicationStatus, uploadDocuments } from 'thunks';
import { ApplicationStatusName } from 'enums/ApplicationStatusName';
import { FlowComponentType } from 'routes/types';
import { DigifiDocumentType } from 'api/DocumentsApi';
import FormNavigation from 'components/FormNavigation';

import styles from './VerifyPayrollDeduction.module.scss';

const FilesPreview = React.lazy(() => import('components/FilesPreview'));

export const DROPBOX_EXTENSIONS = ['image/png', 'image/jpg', 'image/jpeg', 'image/gif', 'application/pdf'];

const VerifyPayrollDeduction = ({ handleNext, navigationInfo }: FlowComponentType) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application } = useSelector(getApplicationData);

  const { id: applicationId } = useSelector(getApplicationData).application!;
  const { handleAddFiles, files, handleRemoveFiles } = useFiles();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleContinue = async () => {
    const filesForUpload: File[] = files.map((item) => item.file);
    setIsLoading(true);
    await dispatchWithUnwrap(
      uploadDocuments({
        applicationId,
        files: filesForUpload,
        fileType: DigifiDocumentType.DepositAccountScreenshot,
      }),
    );

    const shouldUpdateStatus = application?.status !== ApplicationStatusName.FinalReview;

    if (shouldUpdateStatus) {
      await dispatchWithUnwrap(
        updateApplicationStatus({
          applicationId,
          applicationStatus: ApplicationStatusName.FinalReview,
        }),
      );
    }
    setIsLoading(false);

    handleNext();
  };

  return (
    <>
      <FormNavigation {...navigationInfo} />
      <FormContainer
        title="Upload confirmation of deduction"
        subtitle="Please upload a screenshot of your payroll system or confirmation email that shows that the deduction has been set up."
      >
        <div className={styles.fileUploadContainer}>
          <div className={styles.dropzoneContainer}>
            <DropzoneWidget handleFiles={handleAddFiles} extensions={DROPBOX_EXTENSIONS} />
          </div>

          {Boolean(files.length) && (
            <Suspense fallback={<ReactLoading type="spin" color="#fff" />}>
              <FilesPreview files={files} handleRemove={handleRemoveFiles} />
            </Suspense>
          )}
        </div>

        <Button className={styles.button} disabled={!files.length} onClick={handleContinue} isLoading={isLoading}>
          Next
        </Button>
      </FormContainer>
    </>
  );
};

export default VerifyPayrollDeduction;
